import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store/modules/user'
import { dispensingDetail } from '@/api/manage/dispensing'
import { chargePrintAutoSettingList } from '@/api/manage/printAutoSetting'
import { printChargeReceipt, printDispensingReceipt, printRefundReceipt, printPrescription, printTreatment, printMedicationInstruction, printMedicationLabel, printPatientLabel } from './print'
import { backendDocumentType, getPrintScope, getPrinterBridge, type PrintOutputOptions, type PrintDocument } from './printRules'

const getChargeRule = async (document: PrintDocument) => {
  const res = await chargePrintAutoSettingList()
  if (res.code !== 0 || !Array.isArray(res.data)) throw new Error(res.msg || '读取收费自动打印设置失败')
  const item = res.data.find((row: any) => row.documentType === backendDocumentType[document])
  if (!item?.printerName) return undefined
  return {
    deviceName: item.printerName,
    copies: Number(item.printCopies) || 1,
    preview: Boolean(item.previewEnabled),
  }
}

export async function runChargeDocument(id: string | number, document: PrintDocument, output?: PrintOutputOptions, paidOnly = false, linked = false) {
  const config = { isCharge: true }
  if (output) {
    const user = useUserStore()
    const scope = getPrintScope()
    const token = user.getToken
    output = { ...output, isActive: () => Boolean(scope) && getPrintScope() === scope && user.getToken === token }
  }
  switch (document) {
    case 'charge_receipt': return printChargeReceipt(id, config, output)
    case 'dispensing_receipt': return printDispensingReceipt(id, { allowLinkedInstructions: linked && !output }, output)
    case 'refund_receipt': return printRefundReceipt(id, config, output)
    case 'prescription': return printPrescription(id, config, output)
    case 'treatment': return printTreatment(id, 'medical', { configParams: config, medicalRecordParams: config }, output)
    case 'instruction': return printMedicationInstruction(id, paidOnly, output)
    case 'medication_label': return printMedicationLabel(id, config, output)
    case 'patient_label': return printPatientLabel(id, config, output)
  }
}

export async function printChargeDocument(id: string | number, document: PrintDocument, linked = false, paidOnly = false) {
  try {
    const output = await getChargeRule(document)
    const result = await runChargeDocument(id, document, output, paidOnly, linked)
    if (output && linked && result?.status === 'submitted' && document === 'dispensing_receipt' && !(await isMessagePrintEnabled('instruction'))) {
      const setting = await dispensingDetail({})
      if (setting.code === 0 && setting.data?.autoPrintDrugInstructions === true) {
        await printChargeDocument(id, 'instruction', false, true)
      }
    }
    return result
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '打印失败')
  }
}

// The same 3000 message handles both local and remote charge completion. Do not
// additionally print an enabled document from the local payment success callback.
export async function isMessagePrintEnabled(document: PrintDocument) {
  if (!getPrinterBridge()) return false
  const res = await chargePrintAutoSettingList()
  if (res.code !== 0 || !Array.isArray(res.data)) return false
  return Boolean(res.data.some((row: any) => row.documentType === backendDocumentType[document] && row.autoPrintOnChargeCompleted && row.printerName))
}
